"use client";

import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { FaCalendarAlt, FaEnvelope } from "react-icons/fa";
import { FaPhone } from "react-icons/fa6";
import { isLightPage } from "@/lib/light-pages";

const links = [
  { href: "/", label: "Accueil" },
  { href: "/offres", label: "Offres" },
  { href: "/projets", label: "Projets" },
  { href: "/guides", label: "Guides" },
];

const legalLinks = [
  { label: "Mentions légales", href: "/mentions-legales" },
  { label: "Politique de confidentialité", href: "/politique-de-confidentialite" },
  { label: "Conditions générales de vente (CGV)", href: "/cgv" },
  { label: "Conditions générales d'utilisation (CGU)", href: "/cgu" },
];

const CALENDLY_URL =
  "https://calendly.com/webdifference/nouvelle-reunion?hide_gdpr_banner=1&background_color=282828&text_color=ffffff&primary_color=71ddae&month=2025-12";

type SiteFooterProps = {
  /** Fourni par le layout serveur pour éviter toute divergence d’hydratation sur l’année. */
  copyrightYear: number;
};

export function SiteFooter({ copyrightYear }: SiteFooterProps) {
  const pathname = usePathname();
  const lightPage = isLightPage(pathname);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const headingClass = lightPage ? "text-[#1C1C1C]" : "text-white";
  const bodyClass = lightPage ? "text-[#1C1C1C]" : "text-white/85";
  const mutedClass = lightPage ? "text-[#1C1C1C]/75" : "text-white/60";
  const linkClass = (isActive: boolean) =>
    `text-sm transition-all ${bodyClass} ${
      isActive
        ? `underline decoration-2 underline-offset-4 ${lightPage ? "decoration-[#1C1C1C]" : "decoration-white/80"}`
        : `hover:underline decoration-2 underline-offset-4 ${lightPage ? "hover:decoration-[#1C1C1C]/70" : "hover:decoration-white/60"}`
    }`;
  const dividerClass = lightPage ? "border-zinc-900/10" : "border-white/10";

  const floatingShellClass = lightPage
    ? "border border-zinc-200/70 shadow-[0_8px_30px_rgba(0,0,0,0.08),inset_0_1px_0_rgba(255,255,255,0.85)] backdrop-blur-xl backdrop-saturate-150"
    : "border border-white/12 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.55),inset_0_1px_0_rgba(255,255,255,0.1)] backdrop-blur-2xl backdrop-saturate-150";

  const floatingShellStyle = lightPage
    ? { background: "rgba(255, 255, 255, 0.72)" }
    : { background: "rgba(255, 255, 255, 0.08)" };

  return (
    <div className={`safe-bottom px-3 pb-3 pt-4 sm:px-4 sm:pb-4 ${lightPage ? "bg-[#f3f3f3]" : "bg-(--color-background-strong)"}`}>
      <footer
        className={`relative mx-auto max-w-6xl overflow-hidden rounded-2xl py-10 sm:rounded-[1.25rem] sm:py-12 ${floatingShellClass}`}
        style={{
          ...floatingShellStyle,
          WebkitBackdropFilter: "blur(24px) saturate(150%)",
          transform: "translate3d(0, 0, 0)",
          WebkitTransform: "translate3d(0, 0, 0)",
        }}
      >
        <div
          aria-hidden="true"
          className={`pointer-events-none absolute inset-0 ${
            lightPage
              ? "bg-gradient-to-br from-white/40 via-transparent to-emerald-400/8"
              : "bg-gradient-to-br from-white/10 via-transparent to-emerald-400/6"
          }`}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-white/80 to-transparent"
        />

        <div className="relative mx-auto w-full px-5 sm:px-6">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-4">
              <Link href="/" className="inline-block">
                <Image
                  src="/assets/wd-vf.png"
                  alt="Web Difference logo"
                  width={1616}
                  height={949}
                  className="h-auto w-[80px] md:w-[92px]"
                />
              </Link>
              <p className={`text-sm ${bodyClass}`} suppressHydrationWarning>
                Le site web enfin rentable
              </p>
            </div>

            <div className="space-y-3">
              <h3 className={`text-base font-extrabold ${headingClass}`}>Navigation</h3>
              <nav className="flex flex-col gap-2">
                {links.map((link) => {
                  const isActive = mounted && pathname === link.href;
                  return (
                    <Link key={link.href} href={link.href} className={linkClass(isActive)}>
                      {link.label}
                    </Link>
                  );
                })}
              </nav>
            </div>

            <div className="space-y-2">
              <h3 className={`text-base font-extrabold ${headingClass}`}>Contact</h3>
              <div className="flex flex-col gap-1">
                <a
                  href={CALENDLY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`inline-flex items-center gap-2 text-sm transition-all hover:underline decoration-2 underline-offset-4 ${bodyClass}`}
                  suppressHydrationWarning
                >
                  <FaCalendarAlt className={mutedClass} />
                  Prendre RDV
                </a>
                <a
                  href="tel:+33638723038"
                  className={`flex items-center gap-2 text-sm transition-all hover:underline decoration-2 underline-offset-4 ${bodyClass}`}
                >
                  <FaPhone className={mutedClass} />
                  06 38 72 30 38
                </a>
                <a
                  href="mailto:contact@webdifference.fr"
                  className={`flex items-center gap-2 text-sm transition-all hover:underline decoration-2 underline-offset-4 ${bodyClass}`}
                >
                  <FaEnvelope className={mutedClass} />
                  contact@webdifference.fr
                </a>
              </div>
            </div>

            <div className="space-y-2">
              <h3 className={`text-base font-extrabold ${headingClass}`}>Légal</h3>
              <nav className="flex flex-col gap-2">
                {legalLinks.map((link) => (
                  <Link
                    key={link.href}
                    href={link.href}
                    className={`text-sm transition-all hover:underline decoration-2 underline-offset-4 leading-tight flex items-center ${bodyClass} ${lightPage ? "hover:decoration-[#1C1C1C]/70" : "hover:decoration-white/60"}`}
                  >
                    {link.label}
                  </Link>
                ))}
              </nav>
            </div>
          </div>

          <div className={`mt-8 border-t pt-8 text-center ${dividerClass}`}>
            <p className={`text-sm ${bodyClass}`}>
              © {copyrightYear} Web Difference. Tous droits réservés.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
